// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import {
  initialHiLoSummaries,
  initialFrequenciesValues,
  initialFrequencyValues,
} from 'redux/modules/pathDetails'

import {
  castToFloat,
  castToString,
  ifObjectGetValue,
} from 'helpers/reduxFormFunctions'

// if hiLoSummary is null, then define null counts for
// the environemtn and project.
// otherwise just pass through the values the API defined.
const makeHiLoSummaries = hiLoSummary => hiLoSummary || initialHiLoSummaries

const combineFrequencyPlanWithSEF = (f, sef = []) => {
  // TODO - remove once data transformation is complete
  sef.forEach(s => {
    // check to see if the segment end's channel frequency id is actually in the
    // given frequency plan
    const matchFound = !!f.find(ff => ff.channelFrequencyId === s.channelFrequencyId)
    if (!matchFound) {
      throw new Error('Warning! A segment end frequency was assigned which did not correspond to the given frequency plan.')
    }
  })

  return (
    f.map(d => {
      // matches the corresponding segmentEndFrequency, if it's enabled that is.
      // if it's not enabled, takes initialFrequencyValues instead
      const {
        polarizationId,
        segmentEndFrequencyId,
      } = sef.find(s => s.channelFrequencyId === d.channelFrequencyId) || initialFrequencyValues

      // if there's a polarization id, then this frequency is being used on the path.
      // assign its values.
      if (polarizationId) {
        d.polarizationId = castToString(polarizationId)
      }

      d.segmentEndFrequencyId = castToFloat(segmentEndFrequencyId) || null
      d.uniqueId = 'channelNumber'
      return d
    })
  )
}

// if sef is undefined, that means that no custom frequency data was defined,
// if that were the case, this'd just return the default the initialFrequenciesValues
const scrubSEF = sef => (
  initialFrequenciesValues.reduce((a, d, i) => {
    // if !!sef[i], this will be a custom frequency that has been assigned to the
    // segment end. it's possible this will be undefined though. this is because you can
    // have fewer than 13 custom frequencies assigned to a segment end.
    const f = sef[i]

    // since we want to display 13 rows in the UI.
    // all of them will have the initial values for a segment end frequency
    const s = {
      ...d,
      uniqueId: 'channelNumber', // for <DataTable /> to display the frequencies
    }

    // then if f was defined, we want to assign the custom values that were defined here.
    if (f) {
      s.segmentEndFrequencyId = f.segmentEndFrequencyId
      s.channelFrequency = castToString(f.segmentEndFrequency)
      s.polarizationId = castToString(f.polarizationId)
    }
    a.push(s)
    return a
  }, [])
)

// f is frequencyPlan.frequencies,
// sef is segmentEndFrequencies
// if f is undefined, that means we're not dealing with a standard frequency plan
const scrubFrequencies = (f = [], sef) => (
  f.length
    ? combineFrequencyPlanWithSEF(f, sef)
    : scrubSEF(sef)
)

// f is the frequencyPlan entity,
// sef is segmentEndFrequencies
const scrubFrequencyPlanData = (freq = {}, sef, hiLoSummary) => {
  const f = freq || {}

  return ({
    ...makeHiLoSummaries(hiLoSummary),
    frequencies: scrubFrequencies(f.frequencies, sef),
    customFrequencyPlan: !f.frequencyPlanId && !!sef.length, // if there's an id here, these aren't custom frequencies
    frequencyPlan: f.frequencyPlan || '',
    frequencyPlanId: f.frequencyPlanId || null,
    frequencyPlanStored: f.frequencyPlan || '', // this is for custom blur handling on this input
    pairDesignation: ifObjectGetValue(f.pairDesignation, 'pairDesignation'),
  })
}

const frequencyPlanDataForForm = ({ frequencyPlan, segmentEndFrequencies, hiLoSummary }) => (
  scrubFrequencyPlanData(frequencyPlan, segmentEndFrequencies, hiLoSummary)
)

export default frequencyPlanDataForForm
